import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@repo/design-system-kit/components/button/button';
import { type ReactNode, useEffect } from 'react';
import { useSegment } from '@repo/poly-hooks/segment';
import { CFT } from '@repo/runtime-kit/sentry/sentry-tags';
import { useSentry } from '@repo/runtime-kit/sentry/context';
import { persons as kycAcceptance } from '@repo/api-kit/kyc-acceptance';
import { isMobile } from '../../../utils/device';
import { Error404 } from '../../svgs';
import styles from './error.module.css';

const { ApiIdnowProviderDetailsErrorReason } = kycAcceptance.firstparty.v1;

/**
 * Reasons that indicate an integration or provider-side problem.
 * Logged to Sentry as `warning` — these trigger alerts.
 *
 * All other reasons are user-side (bad lighting, wrong ID, user abort, etc.)
 * and are logged as `info` — no alerts, but volume spikes remain visible
 * for threshold-based monitoring.
 */
const INTEGRATION_ERROR_REASONS = new Set<IdNowErrorKeys>([
  'IDNOW_ISSUE',
  'IDNOW_DISCONNECTED',
  'CONNECTION_ISSUES',
  'STALLED_TIMEOUT',
  'QES_ISSUE',
  'NO_AGENTS',
  'CALL_QUALITY_FAIL',
  'IDENT_CODE_NOT_RECEIVED',
]);

// Derived from the proto enum so a new BE reason can't silently miss a copy entry.
export type IdNowErrorKeys = Uppercase<
  Exclude<
    kycAcceptance.firstparty.v1.ApiIdnowProviderDetailsErrorReason,
    typeof ApiIdnowProviderDetailsErrorReason.ERROR_REASON_UNSPECIFIED
  >
>;

export interface IdNowErrorProps {
  onTryAgain: () => void;
  onSwitchToPhone: () => void;
  reason?: IdNowErrorKeys;
}

export function IdNowError({ onTryAgain, onSwitchToPhone, reason }: IdNowErrorProps): ReactNode {
  const intl = useIntl();
  const segment = useSegment();
  const faqHref = intl.formatMessage({ id: 'identity-verification-kit.links.faq' });
  const sentry = useSentry();

  useEffect(() => {
    segment.track('onboarding_idnow_call_aborted', {
      reason: reason ?? 'UNKNOWN_ISSUE',
    });

    const isIntegrationError = reason !== undefined && INTEGRATION_ERROR_REASONS.has(reason);

    sentry.captureMessage('IDnow verification error', {
      level: isIntegrationError ? 'warning' : 'info',
      extra: { reason: reason ?? 'UNKNOWN_ISSUE' },
      tags: { component: 'IdNowError', provider: 'idnow' },
      cft: CFT.DUE_DILIGENCE,
    });
  }, [segment, sentry, reason]);

  const errorMessageIdByReason: Record<IdNowErrorKeys, string> = {
    CONNECTION_ISSUES:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-connection-issues',
    IDNOW_ISSUE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-idnow-issue',
    NO_AUDIO: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-no-audio',
    NO_VIDEO: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-no-video',
    WRONG_LANGUAGE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-wrong-language',
    BAD_AUDIO: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-bad-audio',
    ID_NOT_SUPPORTED:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-id-not-supported',
    GENERIC: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-generic',
    BAD_VIDEO: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-bad-video',
    ID_DAMAGED:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-id-damaged',
    AGENT_ABORT:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-agent-abort',
    USER_ABORT:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-user-abort',
    NO_ID: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-no-id',
    WRONG_PERSON:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-wrong-person',
    IDENT_CODE_NOT_RECEIVED:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-ident-code-not-received',
    IDNOW_DISCONNECTED:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-idnow-disconnected',
    ID_EXPIRED:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-id-expired',
    ID_HOLOGRAM:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-id-hologram',
    LIGHTNING: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-lightning',
    VIDEO_GENERIC_ISSUE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-video-generic-issue',
    BACKGROUND_NOISE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-background-noise',
    AUDIO_GENERIC_ISSUE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-audio-generic-issue',
    WRONG_ID_CODE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-wrong-id-code',
    USER_ABORT_WHILE_WAITING:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-user-abort-while-waiting',
    OTHER_DEVICE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-other-device',
    ID_GENERIC_ISSUE:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-generic',
    STALLED_TIMEOUT:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-generic',
    CALL_QUALITY_FAIL:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-connection-pre-check',
    NO_VIDEO_IDENT_CONSENT:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-no-video-ident-consent',
    WRONG_ID_NUMBER:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-wrong-id-number',
    QES_ISSUE: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-qes-issue',
    WRONG_PHONE_NUMBER:
      'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-wrong-phone-number',
    NO_AGENTS: 'identity-verification-kit.qonto-idnow.error-screen.error-reason.subtitle-no-agents',
  };

  // Runtime safety: host apps can sometimes pass an unknown `reason` string.
  const resolvedErrorMessageId =
    reason && Object.prototype.hasOwnProperty.call(errorMessageIdByReason, reason)
      ? errorMessageIdByReason[reason]
      : errorMessageIdByReason.ID_GENERIC_ISSUE;

  return (
    <div className={styles.wrapper} data-test-idnow-step-error>
      <div className={styles.container}>
        <div className="mb-32">
          <Error404 className={styles.icon} />
        </div>
        <h1 className="heading-section mb-16">
          <FormattedMessage id="identity-verification-kit.qonto-idnow.error-screen.title" />
        </h1>
        <p className="body-standard mb-32" data-test-idnow-error-step-description>
          <FormattedMessage
            // oxlint-disable-next-line @repo/no-dynamic-intl-id
            id={resolvedErrorMessageId}
            values={{
              faqLink: (chunks: ReactNode) => (
                <a className="body-standard-link" href={faqHref} rel="noopener" target="_blank">
                  {chunks}
                </a>
              ),
            }}
          />
        </p>
        <Button
          className="mb-16"
          data-test-idnow-error-step-try-again-btn
          onPress={onTryAgain}
          stretch
          type="button"
        >
          <FormattedMessage id="identity-verification-kit.qonto-idnow.error-screen.primary-cta" />
        </Button>
        {!isMobile && (
          <Button
            className="mb-16"
            data-test-idnow-error-step-switch-to-mobile-btn
            onPress={onSwitchToPhone}
            stretch
            type="button"
            variant="secondary"
          >
            <FormattedMessage id="identity-verification-kit.qonto-idnow.error-screen.secondary-cta" />
          </Button>
        )}
      </div>
    </div>
  );
}
